import { NgClass } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  inject,
  input,
  OnChanges,
  output,
  signal,
  SimpleChanges,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatOptionSelectionChange } from '@angular/material/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import {
  MatOption,
  MatSelect,
  MatSelectModule,
} from '@angular/material/select';
import { debounceTime, distinctUntilChanged, startWith, tap } from 'rxjs';
import { ThemeService } from 'src/app/@services/theme.service';
import { SynMultiSelectOption } from './syn-multi-select-option.model';

@Component({
  selector: 'syn-multi-select',
  templateUrl: './syn-multi-select.component.html',
  styleUrl: './syn-multi-select.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    NgClass,
    MatFormFieldModule,
    MatSelectModule,
    MatIconModule,
    MatCheckboxModule,
    MatButtonModule,
    FormsModule,
    ReactiveFormsModule,
  ],
})
export class SynMultiSelectComponent<T> implements OnChanges {
  private _initialized = false;

  constructor() {
    this.searchFormCtrl.valueChanges
      .pipe(
        takeUntilDestroyed(),
        startWith(this.searchFormCtrl.value),
        tap((value) => this.searchHasText.set(!!String(value ?? '').trim())),
        debounceTime(100),
        distinctUntilChanged(),
        tap((value) => this._filterSearchOptions(value))
      )
      .subscribe();
  }

  protected readonly ALL_OPTION = '(All)';

  private readonly _themeService = inject(ThemeService);

  public matMultiSelectRef = viewChild<MatSelect>('matMultiSelect');

  public options = input<SynMultiSelectOption<T>[] | string[]>();
  public selectedValues = input<SynMultiSelectOption<T>[] | string[]>();
  public labelIcon = input.required<string>();
  public labelText = input.required<string>();
  public labelSearch = input<string>();
  public labelPosition = input<'before' | 'after'>('before');
  public theme = input<'default' | 'transparent'>('default');

  public selectionsApplied = output<SynMultiSelectOption<T>[] | string[]>();

  protected selectFormCtrl = new FormControl();
  protected searchFormCtrl = new FormControl();

  protected readonly searchHasText = signal(false);

  protected filteredOptions = signal<SynMultiSelectOption<T>[] | string[]>([]);

  protected get buttonGradient(): ReturnType<
    typeof this._themeService.buttonGradient
  > {
    return this._themeService.buttonGradient();
  }

  ngOnChanges(changes: SimpleChanges): void {
    this._filterSearchOptions();

    const optionsChanged = !!changes['options'];
    const selectedValuesChange = changes['selectedValues'];
    const selectedValuesChanged =
      !!selectedValuesChange &&
      selectedValuesChange.currentValue !== selectedValuesChange.previousValue;

    if (optionsChanged || selectedValuesChanged) {
      this._initialized = false;
      this._restoreSelectedValues();
    }
  }

  protected displayOption(option: string | SynMultiSelectOption<T>): string {
    return typeof option === 'string' ? option : option.label;
  }

  protected toggleAllChange(
    allOptionItem: MatOption<string>,
    event?: MatOptionSelectionChange<string>
  ): void {
    if (event?.isUserInput) {
      this.selectFormCtrl.setValue(
        allOptionItem.selected ? this.options() : []
      );
      if (allOptionItem.selected) {
        allOptionItem.select();
      }
    }
  }

  protected toggleOptionChange(
    optionItem: MatOption<string | SynMultiSelectOption<T>>,
    event?: MatOptionSelectionChange<string | SynMultiSelectOption<T>>
  ): void {
    if (event?.isUserInput) {
      const formValue = this.selectFormCtrl.value || [];
      const optionValue = optionItem.value;

      if (optionItem.selected) {
        this._addOptionToFormValue(formValue, optionValue);
      } else {
        this._removeOptionFromFormValue(formValue, optionValue);
      }

      if (
        formValue.length === this.options()?.length &&
        this.options()?.length != 1
      ) {
        this._addOptionToFormValue(formValue, this.ALL_OPTION);
      }
    }
  }

  protected onSearchKeydown(event: KeyboardEvent): void {
    if (event.key === ' ') {
      event.stopImmediatePropagation();
    }
  }

  protected clearPanelSearch(): void {
    this.searchFormCtrl.setValue('');
  }

  protected applySelection(): void {
    this.selectionsApplied.emit(
      (this.selectFormCtrl.value ?? []).filter(
        (value: string) => value !== this.ALL_OPTION
      )
    );
    this.matMultiSelectRef()?.close();
  }

  private _addOptionToFormValue(
    formValue: (string | SynMultiSelectOption<T>)[],
    optionValue: string | SynMultiSelectOption<T>
  ): void {
    formValue.push(optionValue);
    this.selectFormCtrl.patchValue(formValue);
  }

  private _removeOptionFromFormValue(
    formValue: (string | SynMultiSelectOption<T>)[],
    optionValue: string | SynMultiSelectOption<T>
  ): void {
    const index = formValue.indexOf(optionValue);
    if (index > -1) {
      formValue.splice(index, 1);
    }
    if (formValue.includes(this.ALL_OPTION)) {
      formValue.splice(formValue.indexOf(this.ALL_OPTION), 1);
    }
    this.selectFormCtrl.patchValue(formValue);
  }

  private _restoreSelectedValues(): void {
    if (this._initialized) {
      return;
    }

    const values = this.selectedValues();
    if (!values || values.length === 0) {
      if (this.selectFormCtrl.value?.length > 0) {
        this.selectFormCtrl.setValue([]);
      }
      return;
    }

    const allOptions = this.options() ?? [];
    if (allOptions.length === 0) {
      return;
    }

    this._initialized = true;

    const validValues = values.filter(
      (value: string | SynMultiSelectOption<T>) =>
        allOptions.some((option: string | SynMultiSelectOption<T>) => {
          if (typeof option === 'string' && typeof value === 'string') {
            return option === value;
          }
          if (typeof option !== 'string' && typeof value !== 'string') {
            return option.value === value.value;
          }
          return false;
        })
    );

    if (validValues.length === 0) {
      if (this.selectFormCtrl.value?.length > 0) {
        this.selectFormCtrl.setValue([]);
      }
      return;
    }

    const formValue: (string | SynMultiSelectOption<T>)[] = [...validValues];

    if (formValue.length === allOptions.length && allOptions.length > 1) {
      formValue.push(this.ALL_OPTION as string | SynMultiSelectOption<T>);
    }

    this.selectFormCtrl.setValue(formValue);
  }

  private _syncAllOptionState(): void {
    if (this.searchFormCtrl.value) {
      return;
    }

    const formValue = this.selectFormCtrl.value;
    if (!formValue || !Array.isArray(formValue)) {
      return;
    }

    const allOptions = this.options() ?? [];
    const selectedNonAll = formValue.filter(
      (v: string | SynMultiSelectOption<T>) => v !== this.ALL_OPTION
    );
    const allSelected =
      selectedNonAll.length === allOptions.length && allOptions.length > 1;
    const hasAllOption = formValue.includes(this.ALL_OPTION);

    if (allSelected && !hasAllOption) {
      this.selectFormCtrl.setValue([
        ...selectedNonAll,
        this.ALL_OPTION as string | SynMultiSelectOption<T>,
      ]);
    } else if (!allSelected && hasAllOption) {
      this.selectFormCtrl.setValue(selectedNonAll);
    }
  }

  private _filterSearchOptions(searchValue?: string): void {
    const options = this.options() ?? [];
    if (!searchValue) {
      this.filteredOptions.set(options);
    } else {
      const filteredStringOptions = options.filter(
        (option): option is string =>
          typeof option === 'string' &&
          option.toLowerCase().includes(searchValue.toLowerCase())
      );

      const filteredObjectOptions = options.filter(
        (option): option is SynMultiSelectOption<T> =>
          typeof option !== 'string' &&
          option.label.toLowerCase().includes(searchValue.toLowerCase())
      );

      this.filteredOptions.set(
        filteredStringOptions.length > 0
          ? filteredStringOptions
          : filteredObjectOptions
      );
    }

    this._syncAllOptionState();
  }
}
